import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { FaSearch, FaTimes, FaHistory, FaFire } from 'react-icons/fa';
import { closeSearch } from '../../redux/slices/uiSlice';
import { fetchAutocomplete, clearAutocomplete } from '../../redux/slices/productSlice';

const SearchOverlay = () => {
  const [query, setQuery] = useState('');
  const [recentSearches, setRecentSearches] = useState([]);
  const inputRef = useRef(null);
  const debounceRef = useRef(null);
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { isSearchOpen } = useSelector((state) => state.ui);
  const { autocompleteSuggestions } = useSelector((state) => state.products);

  const popularSearches = ['Saree', 'Kurti', 'Lehenga', 'Anarkali', 'Salwar Suit', 'Dupatta'];

  useEffect(() => {
    const saved = JSON.parse(localStorage.getItem('rrc_recent_searches') || '[]');
    setRecentSearches(saved);
  }, []);

  useEffect(() => {
    if (isSearchOpen && inputRef.current) {
      setTimeout(() => inputRef.current?.focus(), 100);
    }
    if (!isSearchOpen) {
      setQuery('');
      dispatch(clearAutocomplete());
    }
  }, [isSearchOpen, dispatch]);

  const handleSearchInput = useCallback((value) => {
    setQuery(value);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (value.trim().length > 1) {
      debounceRef.current = setTimeout(() => {
        dispatch(fetchAutocomplete(value.trim()));
      }, 300);
    } else {
      dispatch(clearAutocomplete());
    }
  }, [dispatch]);

  const executeSearch = (searchTerm) => {
    const term = searchTerm || query;
    if (!term.trim()) return;
    const searches = JSON.parse(localStorage.getItem('rrc_recent_searches') || '[]');
    const updated = [term, ...searches.filter((s) => s !== term)].slice(0, 5);
    localStorage.setItem('rrc_recent_searches', JSON.stringify(updated));
    setRecentSearches(updated);
    navigate(`/search?q=${encodeURIComponent(term.trim())}`);
    dispatch(closeSearch());
    setQuery('');
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') executeSearch();
    if (e.key === 'Escape') {
      dispatch(closeSearch());
      setQuery('');
    }
  };

  const clearRecentSearches = () => {
    localStorage.removeItem('rrc_recent_searches');
    setRecentSearches([]);
  };

  if (!isSearchOpen) return null;

  return (
    <div
      style={{
        position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
        background: 'rgba(27, 58, 99, 0.95)', zIndex: 2000,
        display: 'flex', flexDirection: 'column', alignItems: 'center',
        justifyContent: 'flex-start', paddingTop: '80px',
        animation: 'fadeIn 0.3s ease',
      }}
      onClick={(e) => { if (e.target === e.currentTarget) { dispatch(closeSearch()); setQuery(''); } }}
    >
      <button
        onClick={() => { dispatch(closeSearch()); setQuery(''); }}
        style={{ position: 'absolute', top: '20px', right: '30px', background: 'none', border: 'none', color: '#fff', fontSize: '1.5rem', padding: '8px' }}
      >
        <FaTimes />
      </button>

      <div className="container" style={{ maxWidth: '650px', width: '100%', padding: '0 20px' }}>
        <h2 style={{ fontFamily: "'Vollkorn', serif", color: '#fff', textAlign: 'center', marginBottom: '30px', fontSize: '1.8rem' }}>
          Search Our Collection
        </h2>

        <div style={{ position: 'relative', marginBottom: '24px' }}>
          <FaSearch style={{ position: 'absolute', left: '16px', top: '50%', transform: 'translateY(-50%)', color: '#888', fontSize: '1.1rem' }} />
          <input
            ref={inputRef}
            type="text"
            value={query}
            onChange={(e) => handleSearchInput(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Search for sarees, kurtis, lehengas..."
            style={{
              width: '100%', padding: '16px 50px 16px 48px',
              borderRadius: '12px', border: '2px solid rgba(255,255,255,0.2)',
              background: 'rgba(255,255,255,0.1)', color: '#fff',
              fontSize: '1.05rem', outline: 'none',
              fontFamily: "'Poppins', sans-serif",
            }}
          />
          {query && (
            <button
              onClick={() => { setQuery(''); dispatch(clearAutocomplete()); inputRef.current?.focus(); }}
              style={{ position: 'absolute', right: '16px', top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', color: 'rgba(255,255,255,0.6)', fontSize: '1rem' }}
            >
              <FaTimes />
            </button>
          )}
        </div>

        {query.trim().length > 1 && autocompleteSuggestions?.products?.length > 0 && (
          <div style={{ background: '#fff', borderRadius: '12px', overflow: 'hidden', marginBottom: '20px', boxShadow: '0 8px 30px rgba(0,0,0,0.3)' }}>
            {(autocompleteSuggestions.products || []).map((item, idx) => (
              <button
                key={item._id || idx}
                onClick={() => executeSearch(item.name || item)}
                className="d-flex align-items-center w-100 px-4 py-3"
                style={{ background: 'none', border: 'none', borderBottom: '1px solid #f0f0f0', color: '#333', fontSize: '0.95rem', textAlign: 'left' }}
                onMouseEnter={(e) => e.currentTarget.style.background = '#F4EDE4'}
                onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
              >
                <FaSearch style={{ color: '#888', marginRight: '12px', fontSize: '0.85rem' }} />
                {item.name || item}
              </button>
            ))}
          </div>
        )}

        {query.trim().length > 1 && autocompleteSuggestions?.products?.length === 0 && (
          <button
            onClick={() => executeSearch()}
            className="w-100 d-flex align-items-center justify-content-center px-4 py-3"
            style={{ background: '#fff', border: 'none', borderRadius: '12px', color: '#1B3A63', fontSize: '0.95rem', fontWeight: 500, marginBottom: '20px', gap: '10px' }}
          >
            <FaSearch /> Search for &quot;{query}&quot;
          </button>
        )}

        {recentSearches.length > 0 && (
          <div style={{ marginBottom: '24px' }}>
            <div className="d-flex justify-content-between align-items-center mb-3">
              <h6 style={{ color: 'rgba(255,255,255,0.8)', fontFamily: "'Poppins', sans-serif", fontSize: '0.88rem', fontWeight: 500, marginBottom: 0 }}>Recent Searches</h6>
              <button onClick={clearRecentSearches} style={{ background: 'none', border: 'none', color: 'rgba(255,255,255,0.5)', fontSize: '0.8rem' }}>Clear All</button>
            </div>
            <div className="d-flex flex-wrap gap-2">
              {recentSearches.map((search) => (
                <button
                  key={search}
                  onClick={() => executeSearch(search)}
                  style={{ background: 'rgba(255,255,255,0.1)', border: '1px solid rgba(255,255,255,0.2)', borderRadius: '20px', padding: '6px 16px', color: 'rgba(255,255,255,0.8)', fontSize: '0.85rem' }}
                >
                  <FaHistory style={{ marginRight: '6px', fontSize: '0.75rem' }} />
                  {search}
                </button>
              ))}
            </div>
          </div>
        )}

        <div>
          <h6 style={{ color: 'rgba(255,255,255,0.8)', fontFamily: "'Poppins', sans-serif", fontSize: '0.88rem', fontWeight: 500, marginBottom: '12px' }}>Popular Searches</h6>
          <div className="d-flex flex-wrap gap-2">
            {popularSearches.map((search) => (
              <button
                key={search}
                onClick={() => executeSearch(search)}
                style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.15)', borderRadius: '20px', padding: '6px 16px', color: 'rgba(255,255,255,0.7)', fontSize: '0.85rem' }}
              >
                <FaFire style={{ marginRight: '6px', fontSize: '0.75rem', color: '#ff6b35' }} />
                {search}
              </button>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default SearchOverlay;
